import { CompressOutlined, ExpandOutlined } from "@ant-design/icons";
import { html } from "@codemirror/lang-html";
import { monokai } from "@uiw/codemirror-theme-monokai";
import CodeMirror from "@uiw/react-codemirror";
import { Button, Modal } from "antd";
import debounce from "lodash/debounce";
import ImageResize from "quill-image-resize-module-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { addSasToImages, appendSasTokenRichText } from "~/Modules/Centralized/Forms/Imagehelper";
import { post } from "~/Services/api.service";
import { getCurrentModule } from "~/Utils/StringConstants";

// ✅ Register image resize module for Quill
Quill.register("modules/imageResize", ImageResize);
const Size = Quill.import("attributors/style/size");
Size.whitelist = ["small", "normal", "large"];
Quill.register(Size, true);
const Link = Quill.import("formats/link");

class CustomLink extends Link {
  static create(value: string) {
    const node = super.create(value);
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
    return node;
  }
}
CustomLink.blotName = "link";
CustomLink.tagName = "A";

Quill.register(CustomLink, true);

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: any;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, placeholder }) => {
  const [showModal, setShowModal] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [textAreaContent, setTextAreaContent] = useState(value);
  const quillRef = useRef<any>(null);
  const codeMirrorRef = useRef<any>(null);
  const module = getCurrentModule();
  const isCorporate = module === "CORPORATE";
  const showMaximizeUI = !isCorporate;
const [editorValue, setEditorValue] = useState(value || "");
  const lastValueRef = useRef<string>("");

  useEffect(() => {
    lastValueRef.current = value;
    setTextAreaContent(value);
  }, [value]);


  useEffect(() => {
  // Only update editor if external value actually changed
  if (value !== editorValue) {
    setEditorValue(value || "");
  }
}, [value]);

  const [currentModule] = useLocalStorage("currentModule", null);

  // ✅ Fix bullet/numbered lists styling globally
  useEffect(() => {
    const styleId = "quill-bullet-fix";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
        .ql-editor ul {
          list-style-type: disc !important;
          padding-left: 1.5em !important;
          margin-left: 1.5em !important;
        }

        .ql-editor ol {
          list-style-type: decimal !important;
          padding-left: 1.5em !important;
          margin-left: 1.5em !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // ✅ Quill modules
  const modules = useMemo(() => {
    const baseToolbar: any[] = [
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      // [{ size: ["small", false, "large"] }]
    ];

    if (module !== "CORPORATE") {
      baseToolbar.push([{ size: ["small", false, "large"] }]);
    }
    if (module === "CORPORATE") {
      baseToolbar.push(["code-block"]);
    }

    return {
      toolbar: {
        handlers: {
          link: () => handleLinkInsert(),
          image: () => handleImageUpload(),
          "code-block": () => handleModalOpen(),
        },
        container: baseToolbar,
      },

      clipboard: {
        matchVisual: false,
        matchers: [
          [
            "img",
            (node: HTMLImageElement) => {
              const src = node.getAttribute("src") || "";

              const out = currentModule === "CENTRALIZED" ? appendSasTokenRichText(src) : src;

              return { ops: [{ insert: { image: out } }] };
            },
          ],
        ],
      },

      imageResize: {
        parchment: Quill.import("parchment"),
        modules: ["Resize", "DisplaySize", "Toolbar"],
      },
    };
  }, [module, currentModule]);

  // ✅ Link insertion handler
  const handleLinkInsert = () => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    let url = prompt("Enter the URL:");
    if (!url) return;

    // Ensure protocol
    if (!/^https?:\/\//i.test(url)) {
      url = `https://${url}`;
    }

    const range = editor.getSelection(true); // true = force selection even if editor not focused

    if (range) {
      if (range.length === 0) {
        // No text selected → insert placeholder text that is a link
        editor.insertText(range.index, "Link", "link", url, "user");
        editor.setSelection(range.index, 4); // select the word "Link" so user can edit it
      } else {
        // Text selected → apply link format (creates real <a>)
        editor.format("link", url, "user");
      }
    }
  };

  // ✅ Image upload handler
  const handleImageUpload = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files ? input.files[0] : null;
      if (file && quillRef.current) {
        const editor = quillRef.current.getEditor();
        const range = editor.getSelection();

        const formData = new FormData();
        formData.append("upload-file", file);
        formData.append("application", module);

        try {
          const response: any = await post("management/upload-file", formData);
          if (response.status === "OK") {
            const imageUrl = response.response.data;
            const imageWithSas = appendSasTokenRichText(imageUrl);
            const finalImage = currentModule === "CENTRALIZED" ? imageWithSas : imageUrl;
            editor.insertEmbed(range.index, "image", finalImage);
          } else {
            console.error("File upload failed:", response.statusText);
          }
        } catch (error) {
          console.error("Error uploading file:", error);
        }
      }
    };
  };

  // ✅ HTML modal open handler
  const handleModalOpen = useCallback(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const currentContent = editor.root.innerHTML;
      setTextAreaContent(currentContent);
    }
    setShowModal(true);
  }, []);

  const handleModalClose = useCallback(() => setShowModal(false), []);

  const handleSave = useCallback(() => {
    const cleanedContent = cleanUpEditorContent(textAreaContent);
    onChange(cleanedContent);
    setTextAreaContent(cleanedContent);
    setShowModal(false);
  }, [textAreaContent, onChange]);

  const cleanUpEditorContent = (content: string) => {
    let clean = content.replace(/<\/?(script|style)[^>]*>/gi, "");
    const allowedTags = [
      "b",
      "i",
      "u",
      "strong",
      "em",
      "a",
      "img",
      "p",
      "div",
      "ol",
      "ul",
      "li",
      "br",
      "code",
      "span",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
    ];

    clean = clean.replace(/<(\/?)(\w+)([^>]*)>/g, (match, slash, tag) =>
      allowedTags.includes(tag.toLowerCase()) ? match : ""
    );
    clean = clean.replace(/ {2,}/g, (match) => "&nbsp;".repeat(match.length));
    return clean.trim();
  };

  // ✅ Handle Quill changes
  // const handleChange = useCallback(
  //   (content: string) => {
  //     const normalized = currentModule === "CENTRALIZED" ? addSasToImages(content) : content;

  //     setTextAreaContent(normalized);
  //     onChange(normalized);
  //   },
  //   [onChange, currentModule]
  // );

  const handleChange = useCallback(
  (
    content: string,
    delta: any,
    source: string,
    editor: any
  ) => {
    // IMPORTANT:
    // Ignore Quill internal updates
    if (source !== "user") return;

    const normalized =
      currentModule === "CENTRALIZED"
        ? addSasToImages(content)
        : content;

    setEditorValue(normalized);

    // Send to parent
    onChange(normalized);
  },
  [onChange, currentModule]
);
  // ✅ Handle CodeMirror changes (debounced)
  const debouncedCodeMirrorChange = useMemo(
    () =>
      debounce((newContent: string) => {
        const cleanedContent = cleanUpEditorContent(newContent);
        setTextAreaContent(cleanedContent);
        onChange(cleanedContent);
      }, 300),
    [onChange]
  );

  const handleCodeMirrorChange = useCallback(
    (newContent: string) => debouncedCodeMirrorChange(newContent),
    [debouncedCodeMirrorChange]
  );

  useEffect(() => {
    if (showModal && codeMirrorRef.current) {
      setTimeout(() => codeMirrorRef.current.view.focus(), 100);
    }
  }, [showModal]);

  const handleBlur = () => {
    const cleaned = cleanUpEditorContent(textAreaContent);
    onChange(cleaned);
  };

  return (
    <div className="relative flex gap-2 mb-6 pb-8">
      {/* ✅ Maximize / Minimize Button */}
      <Button
        type="text"
        icon={isMaximized ? <CompressOutlined /> : <ExpandOutlined />}
        onClick={() => setIsMaximized((prev) => !prev)}
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 1000,
          color: "#555",
        }}
      />

      {/* ✅ Single Quill Editor (shared for both modes) */}
      <div
        style={{
          position: isMaximized ? "fixed" : "relative",
          top: isMaximized ? 0 : "auto",
          left: isMaximized ? 0 : "auto",
          width: isMaximized ? "100vw" : "100%",
          height: isMaximized ? "100vh" : "300px", // Make sure this is enough height
          backgroundColor: "#fff",
          zIndex: isMaximized ? 2000 : 1,
          padding: isMaximized ? "20px" : "0",
          overflow: "hidden", // ✅ No outer scrollbar
          boxSizing: "border-box", // ✅ include borders in height calc
          border: "1px solid #d9d9d9", // ✅ force border around the editor
          borderRadius: "4px", // optional
        }}
      >
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={editorValue}
          onChange={handleChange}
          modules={modules}
          onBlur={handleBlur}
          placeholder={placeholder}
          style={{
            height: isMaximized ? "85vh" : "260px",
            marginBottom: "10px",
            width: "100%",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            boxSizing: "border-box",
            border: "none",
          }}
        />

        {isMaximized && (
          <Button
            type="default"
            icon={<CompressOutlined />}
            onClick={() => setIsMaximized(false)}
            style={{
              position: "absolute",
              top: 10,
              right: 20,
              zIndex: 2100,
            }}
          >
            Minimize
          </Button>
        )}
      </div>

      {/* ✅ HTML Code Editor Modal */}
      <Modal
        title="Edit HTML"
        open={showModal}
        onOk={handleSave}
        onCancel={handleModalClose}
        width={800}
      >
        <CodeMirror
          ref={codeMirrorRef}
          value={textAreaContent}
          onChange={handleCodeMirrorChange}
          height="300px"
          extensions={[html()]}
          theme={monokai}
        />
      </Modal>
    </div>
  );
};

export default RichTextEditor;
